import CryptoJS from 'crypto-js'

// Secret key for encryption (use environment variable in production)
const SECRET_KEY = 'OVACS_SECRET_KEY_2024'

/**
 * Encrypt data using AES encryption
 * @param {string} data - Data to encrypt
 * @returns {string} - Encrypted data as string
 */
export const encryptData = (data) => {
  try {
    if (!data) return ''
    const encrypted = CryptoJS.AES.encrypt(data, SECRET_KEY).toString()
    return encrypted
  } catch (error) {
    console.error('Encryption error:', error)
    return ''
  }
}

/**
 * Decrypt data using AES decryption
 * @param {string} encryptedData - Encrypted data string
 * @returns {string} - Decrypted data
 */
export const decryptData = (encryptedData) => {
  try {
    if (!encryptedData) return ''
    const decrypted = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY).toString(CryptoJS.enc.Utf8)
    return decrypted
  } catch (error) {
    console.error('Decryption error:', error)
    return ''
  }
}

/**
 * Secure token storage with encryption
 */
export const tokenStorage = {
  /**
   * Store user info securely in localStorage
   * @param {Object} userInfo - User information object
   */
  setUserInfo: (userInfo) => {
    try {
      const encryptedData = encryptData(JSON.stringify(userInfo))
      localStorage.setItem('userInfo', encryptedData)
    } catch (error) {
      console.error('Error storing user info:', error)
    }
  },

  /**
   * Retrieve and decrypt user info from localStorage
   * @returns {Object|null} - User information object or null
   */
  getUserInfo: () => {
    try {
      const encryptedData = localStorage.getItem('userInfo')
      if (!encryptedData) {
        console.log('No encrypted data found in localStorage')
        return null
      }
      
      const decryptedData = decryptData(encryptedData)
      if (!decryptedData) {
        console.log('Failed to decrypt user data')
        return null
      }
      
      const userInfo = JSON.parse(decryptedData)
      console.log('Retrieved user info:', { 
        hasToken: !!userInfo.token, 
        userId: userInfo.id, 
        userName: userInfo.name 
      })
      return userInfo
    } catch (error) {
      console.error('Error retrieving user info:', error)
      // Clear corrupted data
      localStorage.removeItem('userInfo')
      return null
    }
  },

  /**
   * Remove user info from localStorage
   */
  removeUserInfo: () => {
    try {
      localStorage.removeItem('userInfo')
    } catch (error) {
      console.error('Error removing user info:', error)
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} - Authentication status
   */
  isAuthenticated: () => {
    const userInfo = tokenStorage.getUserInfo()
    return !!(userInfo && userInfo.token)
  },

  /**
   * Get current access token
   * @returns {string|null} - Access token or null
   */
  getToken: () => {
    const userInfo = tokenStorage.getUserInfo()
    return userInfo ? userInfo.token : null
  },

  /**
   * Update workspace switching info
   * @param {number|null} ownerAccountId - Owner account ID for workspace switching
   * @param {string|null} ownerAccountName - Owner account name
   */
  setWorkspace: (ownerAccountId, ownerAccountName, switchedRole = null) => {
    const userInfo = tokenStorage.getUserInfo()
    if (userInfo) {
      const updatedInfo = {
        ...userInfo,
        ownerAccountId,
        ownerAccountName,
        switchedRole,
      }
      tokenStorage.setUserInfo(updatedInfo)
    }
  },

  /**
   * Clear workspace switching (return to own account)
   */
  clearWorkspace: () => {
    const userInfo = tokenStorage.getUserInfo()
    if (userInfo) {
      const updatedInfo = {
        ...userInfo,
        ownerAccountId: null,
        ownerAccountName: null,
        switchedRole: null,
      }
      tokenStorage.setUserInfo(updatedInfo)
    }
  },
}
